/**
 * useEventStream — subscribe to ws-gateway and stream live events.
 *
 * Wire format from ws-gateway: each WS text frame is one JSON event
 * envelope (the same shape that flows through NATS — `event_type`,
 * `correlation_id`, `tenant_id`, `payload`, etc.). A keepalive frame
 * with `event_type === "ws.heartbeat"` is sent every ~30s; we filter
 * those out before invoking the consumer callback.
 *
 * Reconnect: simple exponential backoff (1s, 2s, 4s, ... up to 30s).
 * The hook is pure: it does not store the events itself — callers do
 * (the right buffer size depends on the screen, e.g. last 50 vs all).
 */

import { useEffect, useRef, useState } from "react";

import { getAccessToken } from "../auth/store";


const DEFAULT_WS_BASE_URL =
  (import.meta as unknown as { env: Record<string, string | undefined> }).env
    .VITE_WS_BASE_URL ?? "ws://localhost:5104";


export interface EventEnvelope {
  event_type: string;
  correlation_id?: string;
  tenant_id?: string;
  payload?: Record<string, unknown>;
  /** Producer-side timestamp (ISO). Not always present. */
  created_at?: string;
  [key: string]: unknown;
}


export type ConnectionState =
  | "idle"
  | "connecting"
  | "open"
  | "closed"
  | "error";


export interface UseEventStreamOptions {
  /** Subject patterns relative to tenant. Default: `[">"]` (all). */
  subjects?: string[];
  /** If false, the WS isn't opened. Useful for conditional subscribe. */
  enabled?: boolean;
}


export interface UseEventStreamResult {
  state:        ConnectionState;
  /** Number of events received since the latest connect (excludes heartbeats). */
  receivedCount: number;
  /** Force the WS to reconnect. */
  reconnect: () => void;
}


/**
 * Subscribe to a NATS-backed event stream over WebSocket.
 *
 * `onEvent` is called once per non-heartbeat frame. The hook keeps
 * a ref to the latest callback, so it's safe to pass an inline lambda
 * from a parent component without re-opening the socket on every render.
 */
export function useEventStream(
  onEvent: (event: EventEnvelope) => void,
  options: UseEventStreamOptions = {},
): UseEventStreamResult {
  const enabled  = options.enabled ?? true;
  const subjects = options.subjects ?? [">"];

  const [state,         setState]         = useState<ConnectionState>("idle");
  const [receivedCount, setReceivedCount] = useState<number>(0);
  const [reconnectKey,  setReconnectKey]  = useState<number>(0);

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) {
      setState("idle");
      return;
    }

    // Sprint 7 — tenant comes from the JWT, never from URL.
    const token = getAccessToken();
    if (!token) {
      setState("closed");
      return;
    }

    const url = new URL(`${DEFAULT_WS_BASE_URL}/v1/events`);
    url.searchParams.set("subjects", subjects.join(","));

    let ws: WebSocket | null = null;
    let cancelled = false;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = (): void => {
      if (cancelled) return;
      setState("connecting");
      // ws-gateway reads the bearer from Sec-WebSocket-Protocol.
      // Two-element subprotocol list is the WHATWG-compliant browser shape:
      // the first identifies the protocol, the second carries the token.
      ws = new WebSocket(url.toString(), ["vseh.bearer", token]);

      ws.onopen = () => {
        if (cancelled) return;
        attempt = 0;
        setState("open");
        setReceivedCount(0);
      };

      ws.onmessage = (ev: MessageEvent<string>) => {
        if (cancelled) return;
        let parsed: EventEnvelope;
        try {
          parsed = JSON.parse(ev.data) as EventEnvelope;
        } catch {
          // Server promised JSON, but we shouldn't crash if a stray
          // frame slips through — just ignore it.
          return;
        }
        if (parsed.event_type === "ws.heartbeat") return;
        setReceivedCount((c) => c + 1);
        try {
          onEventRef.current(parsed);
        } catch {
          // Consumer threw; we don't want that to take the socket down.
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        setState("error");
      };

      ws.onclose = () => {
        if (cancelled) return;
        setState("closed");
        // Exponential backoff capped at 30s. attempt 0 → 1s, 1 → 2s, ...
        const delay = Math.min(30_000, 1_000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, subjects.join(","), reconnectKey]);

  return {
    state,
    receivedCount,
    reconnect: () => setReconnectKey((k) => k + 1),
  };
}
